import { Callout, Selector } from '@casumo/dojo-react';
import { Close } from '@casumo/dojo-react-icons';
import { memo, useMemo, useState } from 'react';
import useWelcomeOffers from '../api/hooks/useWelcomeOffers.tsx';
import Drawer from '../design-system/drawer.tsx';
import Skeleton from '../design-system/skeleton.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { TWoCalloutField } from '../types/types.ts';
import { TEST_ID } from '../utils/constants.ts';
import { GtmEvents } from '../utils/gtm/gtm.ts';

const WoCallout = ({ noBonusSelect, dialogTitle }: TWoCalloutField) => {
  const { metadata, formData, setFormData } = useRegistrationStore();

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (value: string | null) => {
    setFormData({ welcomeOffer: value });
    setIsOpen(false);
    GtmEvents.handleWelcomeOfferEvent(value, metadata?.currentStep);
  };

  const { offers, loading, error } = useWelcomeOffers();

  const selectedBonus = useMemo(() =>
    offers.find(wo => wo.name === formData?.welcomeOffer),
  [offers, formData?.welcomeOffer]);

  if (loading) {
    return <Skeleton height="h-[70px]" />;
  }

  if (!selectedBonus) {
    return;
  }

  if (error) {
    return;
  }

  return (
    <>
      <Callout
        alert="info"
        icon={<img className="w-[38px] h-[38px]"
          data-testid={TEST_ID.IMAGE + '-wo-' + selectedBonus.name}
          src={selectedBonus?.tokenImage}
          alt={selectedBonus?.name} />}
        className="cursor-pointer items-center focus:outline-purpleblue [&>*:nth-child(2)]:pl-xs"
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen(true)}
        tabIndex={0}
        data-testid={TEST_ID.SELECT + selectedBonus.name}
      >
        <div className="flex-wrap text-body-sm"
          data-testid={TEST_ID.TEXT + '-wo-' + selectedBonus.name}
        >
          <span className="font-bold">{selectedBonus?.widgetTitle}:</span>
          <span>&nbsp;{selectedBonus?.shortDesc}</span>
        </div>
      </Callout>
      <Drawer dialogClass="!h-fit" isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="flex flex-col h-full w-full overflow-hidden">
          <div className="flex justify-between text-white pt-[20px] px-md font-bold">
            <h1 className="text-h-lg">{dialogTitle}</h1>
            <span className="p-2xs cursor-pointer text-[24px]" onClick={() => setIsOpen(false)}><Close /></span>
          </div>
          <Selector
            name="welcomeOffer"
            direction="vertical"
            variant="dark"
            className="w-full px-md py-[20px]"
          >
            {offers.map(wo =>
              <Selector.Radio
                key={wo.name}
                className="flex items-center gap-sm font-[450]"
                onClick={() => handleClick(wo.name)}
                defaultChecked={formData?.welcomeOffer === wo.name}
                data-testid={TEST_ID.PREFIX + wo.name}
              >
                <img className="w-[80px] h-[80px]" src={wo?.tokenImage}
                  alt={wo.name} />
                <div>
                  <span
                    className="font-semibold">
                    {wo.title}
                  </span>
                  : {wo.shortDesc}</div>
              </Selector.Radio>,
            )}
            <Selector.Radio className="text-center"
              onClick={() => handleClick(null)}
              data-testid={TEST_ID.BUTTON_NO_BONUS}
            >
              {noBonusSelect}
            </Selector.Radio>
          </Selector>
        </div>
      </Drawer>
    </>
  );
};

const Memoized = memo(WoCallout);
Memoized.displayName = 'WoCallout';

export default Memoized;
